/**
 * Phase 2b — /docs landing page.
 *
 * IA front door: 4-letter eyebrow, decryption-revealed H1, intro line, a
 * grid of section entry tiles built from /api/wiki/nav.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
} from "lucide-react";

import DocsLayout from "../../components/docs/DocsLayout";
import DecryptedHeading from "../../components/docs/DecryptedHeading";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import usePageMeta from "../../hooks/usePageMeta";
import { styleForSection } from "../../lib/sectionStyles";

const API = process.env.REACT_APP_BACKEND_URL;


export default function DocsLanding() {
  useDocumentTitle("Docs — QorTrace");
  usePageMeta({
    title: "Docs — QorTrace",
    description: "QorTrace documentation: methodology, SOC 2 evidence pack, hash-chained audit logs, live trust posture, Q-Day Tax Clock, CBOM scanning, and more.",
    canonicalPath: "/docs",
  });
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/wiki/nav`, { withCredentials: true });
        // Iter127 — Track C: bump "Methodology" to the top of the landing-tile
        // grid so auditors / institutional readers see the credibility doc
        // first. We do NOT mutate the sidebar / Cmd+K ordering (those still
        // use the server-side curated order).
        const raw = r.data?.nodes || [];
        const isMethodology = (s) => /methodology/i.test(s?.title || "");
        const ordered = [
          ...raw.filter(isMethodology),
          ...raw.filter((s) => !isMethodology(s)),
        ];
        setSections(ordered);
      } catch {
        setSections([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <DocsLayout currentSlug={null}>
      <div className="pt-8 pb-24" data-testid="docs-landing" data-docs-wide="true">
        {/* Hero */}
        <div className="relative">
          <div className="qt-docs-landing-glow" aria-hidden="true" />
          <div
            className="qt-docs-eyebrow"
            style={{ color: "var(--qt-cyan)", marginBottom: "0.75rem" }}
          >
            Documentation //
          </div>
          <DecryptedHeading
            target="QorTrace Documentation"
            testId="docs-landing-title"
          />
          <p
            className="text-base md:text-lg leading-relaxed mt-4 max-w-2xl"
            style={{ color: "#A8B7CC" }}
          >
            Everything you need to install, integrate, and reason about QorTrace —
            our scoring methodology, audit pipeline, SDK reference, and enterprise
            engagement model. Authored in markdown, versioned in MongoDB, free to
            read.
          </p>
        </div>

        {/* Section tiles */}
        <div className="mt-12">
          <div
            className="qt-docs-eyebrow mb-4"
            style={{ color: "#4B5B76" }}
          >
            Browse by section
          </div>
          {loading ? (
            <p className="font-mono text-sm" style={{ color: "var(--qt-text-3)" }}>
              Loading…
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {sections.map((sec, i) => {
                const { Icon, color } = styleForSection(sec.title);
                const firstLeaf = (sec.children || [])[0];
                if (!firstLeaf) return null;
                return (
                  <Link
                    key={`${sec.title}-${i}`}
                    to={`/docs/${firstLeaf.slug}`}
                    data-testid={`docs-section-tile-${i}`}
                    className="qt-docs-tile group"
                    style={{ "--tile-accent": color }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <Icon
                        size={20}
                        strokeWidth={1.25}
                        className="qt-docs-tile-icon"
                        style={{ color }}
                      />
                      <ArrowRight
                        size={14}
                        className="qt-docs-tile-arrow"
                      />
                    </div>
                    <div
                      className="font-heading font-bold text-[15px] leading-tight"
                      style={{ color: "var(--qt-text-1)" }}
                    >
                      {sec.title}
                    </div>
                    <div
                      className="qt-mono text-[10px] mt-1"
                      style={{ color, letterSpacing: "0.14em", opacity: 0.85 }}
                    >
                      {(sec.children || []).length} {(sec.children || []).length === 1 ? "page" : "pages"}
                    </div>
                    <ul className="mt-3 space-y-1">
                      {(sec.children || []).slice(0, 3).map((leaf) => (
                        <li
                          key={leaf.slug}
                          className="text-[13px] leading-snug truncate"
                          style={{ color: "#8B9BB4" }}
                        >
                          · {leaf.title}
                        </li>
                      ))}
                      {(sec.children || []).length > 3 && (
                        <li
                          className="text-xs font-mono"
                          style={{ color: "#4B5B76" }}
                        >
                          + {sec.children.length - 3} more
                        </li>
                      )}
                    </ul>
                  </Link>
                );
              })}
              {/* Phase 12.2f — "+ See all sections" filler tile.
               *
               * Renders when the 4-column grid would leave an awkward
               * gap at the end (sections-mod-4 != 0). On hover the tile
               * lights up with the search-action gradient; clicking it
               * fires Cmd+K so the user lands directly in the palette
               * with a "browse everything" mindset. Hidden when the
               * tile count is already a multiple of 4 (no gap to fill)
               * or when there are 0 sections (loading state).
               */}
              {sections.length > 0 && (sections.length % 4 !== 0) && (
                <button
                  type="button"
                  data-testid="docs-see-all-sections-tile"
                  onClick={() => {
                    // Reuse the existing Cmd+K palette wiring — we
                    // dispatch a synthetic keydown so the global
                    // listener in DocsLayout opens the palette without
                    // us needing a new context handle. Safari + Chrome
                    // + Firefox all honour the metaKey property.
                    const ev = new KeyboardEvent("keydown", {
                      key: "k",
                      code: "KeyK",
                      metaKey: true,
                      ctrlKey: true,
                      bubbles: true,
                    });
                    window.dispatchEvent(ev);
                  }}
                  className="qt-docs-tile qt-docs-tile-see-all"
                  style={{ "--tile-accent": "var(--qt-cyan, #00E5FF)" }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className="qt-docs-tile-icon" aria-hidden="true">+</span>
                    <ArrowRight size={14} className="qt-docs-tile-arrow" />
                  </div>
                  <div
                    className="font-heading font-bold text-[15px] leading-tight"
                    style={{ color: "var(--qt-text-1)" }}
                  >
                    See all sections
                  </div>
                  <div
                    className="qt-mono text-[10px] mt-1"
                    style={{ color: "var(--qt-cyan)", letterSpacing: "0.14em", opacity: 0.85 }}
                  >
                    {sections.length} sections · {sections.reduce((sum, s) => sum + (s.children?.length || 0), 0)} pages
                  </div>
                  <div
                    className="text-[13px] mt-3"
                    style={{ color: "#8B9BB4", lineHeight: 1.5 }}
                  >
                    Open the search palette to browse, filter, or jump straight to any page.
                  </div>
                  <div
                    className="qt-mono text-[11px] mt-3 inline-flex items-center gap-2"
                    style={{ color: "var(--qt-cyan)" }}
                  >
                    <kbd className="qt-docs-kbd" aria-hidden="true">⌘ K</kbd>
                    <span style={{ color: "var(--qt-text-3)" }}>or</span>
                    <kbd className="qt-docs-kbd" aria-hidden="true">/</kbd>
                  </div>
                </button>
              )}
            </div>
          )}

          {/* Phase 12.2f — subtle search-nudge below the tile grid.
           * Renders only when sections actually loaded. The tile itself
           * is also a CMD+K entry, but this line catches the user who
           * scrolled past the grid without spotting it. */}
          {!loading && sections.length > 0 && (
            <p
              className="qt-mono text-xs mt-7 text-center"
              data-testid="docs-cmdk-nudge"
              style={{ color: "var(--qt-text-3)", letterSpacing: "0.06em" }}
            >
              Looking for something specific? Press{" "}
              <kbd className="qt-docs-kbd-inline">⌘ K</kbd>{" "}
              <span style={{ opacity: 0.6 }}>or</span>{" "}
              <kbd className="qt-docs-kbd-inline">/</kbd>{" "}
              to search every page.
            </p>
          )}
        </div>

        {/* Trust strip */}
        <div
          className="mt-16 pt-8"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { label: "Methodology version", value: "qortrace-method-v0.2", testid: "docs-trust-methodology" },
              { label: "Public APIs", value: "REST + RSS + verify", testid: "docs-trust-apis" },
              { label: "Last updated", value: new Date().toISOString().slice(0, 10), testid: "docs-trust-updated" },
            ].map((t) => (
              <div key={t.label} data-testid={t.testid}>
                <div
                  className="qt-docs-eyebrow"
                  style={{ color: "#4B5B76", marginBottom: "0.25rem" }}
                >
                  {t.label}
                </div>
                <div
                  className="font-mono text-sm"
                  style={{ color: "var(--qt-text-1)" }}
                >
                  {t.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DocsLayout>
  );
}
